// v0.8 — Weekly Mutators, Combat Modifiers, Tag Synergies, Relic Sets

// ==================== WEEKLY MUTATORS ====================
// Rotate deterministically by ISO week — same for everyone globally.
export const WEEKLY_MUTATORS = [
  {
    id: 'double_damage', name: 'DOUBLE DAMAGE', art: '💥',
    desc: 'All damage (yours & enemies) is doubled.',
    apply: (mods) => { mods.playerDamageMult = (mods.playerDamageMult || 1) * 2; mods.enemyDmgMult = (mods.enemyDmgMult || 1) * 2; }
  },
  {
    id: 'no_relics', name: 'NO RELICS', art: '🚫',
    desc: 'Shops sell no relics. Elites/bosses give gold instead.',
    apply: (mods) => { mods.noRelicRewards = true; }
  },
  {
    id: 'glass_cannon', name: 'GLASS CANNON', art: '💎',
    desc: '2× damage dealt, but you have 30 HP max.',
    apply: (mods) => { mods.playerDamageMult = 2; mods.hpDelta = -30; }
  },
  {
    id: 'rich_boi', name: 'RICH BOI', art: '💰',
    desc: '2× gold from everything. Everything in shops costs 2×.',
    apply: (mods) => { mods.goldMult = 2; mods.shopPriceMult = 2; mods.startGold = 100; }
  },
  {
    id: 'speedrun', name: 'SPEEDRUN OR DIE', art: '⏱',
    desc: 'You have 5 turns to win each fight or you die.',
    apply: (mods) => { mods.turnLimit = 5; }
  },
  {
    id: 'card_chaos', name: 'CARD CHAOS', art: '🎲',
    desc: 'Cards have random energy costs (0-3) each turn.',
    apply: (mods) => { mods.randomizeCosts = true; }
  },
  {
    id: 'tag_tax', name: 'TAG TAX', art: '🏷',
    desc: 'Non-MOG cards cost +1 energy.',
    apply: (mods) => { mods.tagTax = { tag: 'mog' }; }
  },
  {
    id: 'ethereal_everything', name: 'ETHEREAL EVERYTHING', art: '👻',
    desc: 'All cards become ETHEREAL (exhaust if unplayed).',
    apply: (mods) => { mods.forceEthereal = true; }
  },
];

// Deterministic rotation: hash weekKey → index
export function mutatorForWeek(weekKey) {
  if (!weekKey) return WEEKLY_MUTATORS[0];
  let h = 0;
  for (let i = 0; i < weekKey.length; i++) { h = ((h << 5) - h) + weekKey.charCodeAt(i); h |= 0; }
  const idx = Math.abs(h) % WEEKLY_MUTATORS.length;
  return WEEKLY_MUTATORS[idx];
}

// ==================== COMBAT MODIFIERS ====================
// Random pre-combat modifier that appears occasionally in normal fights.
export const COMBAT_MODIFIERS = [
  { id: 'iron_wall', name: 'IRON WALL', desc: 'Enemy starts with +25 block.', apply: (enemy) => { enemy.startBlock = (enemy.startBlock || 0) + 25; } },
  { id: 'blood_rush', name: 'BLOOD RUSH', desc: 'Enemy has +50% HP.', apply: (enemy) => { enemy.maxHp = Math.floor(enemy.maxHp * 1.5); } },
  { id: 'furious', name: 'FURIOUS', desc: 'Enemy starts with +3 STR.', apply: (enemy) => { enemy.strength = (enemy.strength || 0) + 3; } },
  { id: 'plated', name: 'PLATED', desc: 'Enemy gains 4 block each turn.', apply: (enemy) => { enemy.blockPerTurn = 4; } },
  { id: 'lucky_draw', name: 'LUCKY DRAW', desc: 'You draw +1 card each turn.', apply: (enemy, mods) => { mods._extraDraw = 1; }, playerBoon: true },
  { id: 'high_stakes', name: 'HIGH STAKES', desc: '2× gold reward on win.', apply: (enemy, mods) => { mods._rewardGoldMult = 2; }, playerBoon: true },
];

export function rollCombatModifier(rng, chance = 0.15) {
  if (rng() > chance) return null;
  return COMBAT_MODIFIERS[Math.floor(rng() * COMBAT_MODIFIERS.length)];
}

// ==================== TAG SYNERGIES ====================
// When you play 3+ cards of the same tag in a single turn, trigger a bonus.
export const TAG_SYNERGIES = {
  rizz: { name: 'RIZZ CHAIN', threshold: 3, art: '💫', desc: '+1 dmg to all attacks this turn.', apply: (state) => { state.strength += 1; } },
  mog: { name: 'MOG STACK', threshold: 3, art: '🗿', desc: '+2 STR permanently.', apply: (state) => { state.player.strength += 2; } },
  ratio: { name: 'MASS RATIO', threshold: 3, art: '📉', desc: 'Apply 3 WEAK+VULN.', apply: (state) => { state.enemy.weak += 3; state.enemy.vulnerable += 3; } },
  copium: { name: 'COPIUM STORM', threshold: 3, art: '🧊', desc: 'Gain 12 BLOCK.', apply: (state) => { state.player.block += 12; } },
  schizo: { name: 'SCHIZO SPIRAL', threshold: 3, art: '🧠', desc: 'Draw 2 cards.', apply: (state) => { /* draw handled in engine */ state._synergyDraw = (state._synergyDraw || 0) + 2; } },
  doom: { name: 'DOOM FRACTAL', threshold: 3, art: '☠', desc: 'Enemy loses 20% max HP.', apply: (state) => { const chunk = Math.floor(state.enemy.maxHp * 0.20); state.enemy.hp = Math.max(0, state.enemy.hp - chunk); } },
};

// ==================== RELIC SETS ====================
// Owning specific combinations of relics unlocks passive set bonuses.
export const RELIC_SETS = [
  { id: 'streamer_kit', name: 'STREAMER KIT', pieces: ['ringlight','microphone','greenscreen'], bonus: '+2 STR/combat, +25% gold', apply: (mods) => { mods.strPerCombat = (mods.strPerCombat || 0) + 2; mods.goldMult = (mods.goldMult || 1) * 1.25; } },
  { id: 'gigachad', name: 'GIGACHAD SET', pieces: ['proteinshake','miogtrophy','flexcam'], bonus: '+30% dmg + start with 10 block', apply: (mods) => { mods.playerDamageMult = (mods.playerDamageMult || 1) * 1.3; mods.startBlockBonus = (mods.startBlockBonus || 0) + 10; } },
  { id: 'schizo_meta', name: 'SCHIZO META', pieces: ['tinfoilhat','conspiracymap','redstring'], bonus: '+1 energy each turn', apply: (mods) => { mods.energyDelta = (mods.energyDelta || 0) + 1; } },
];

export function activeRelicSets(playerRelics) {
  const active = [];
  for (const set of RELIC_SETS) {
    if (set.pieces.every(p => playerRelics.includes(p))) active.push(set);
  }
  return active;
}
